import { Component, Host, Prop, State, h, Element } from '@stencil/core';
import { SportslinePickProps, ClientName } from 'src/types';
import {
  fromFreeExpertPickToTrackingViewedWidgetDataBet,
  fromSportslineWebComponent_FreeExpertPickQueryToSportslinePickProps,
  getCardTitle,
  getPropertyGameHref,
  getPropertyGameUrlTarget,
  getPropertySubscribeLink,
  getPropertyUpsellText,
} from '@utils/expertPickUtils';
import { sportslineGraphQLClient } from '@lib/graphql-client';
import { SportslineWebComponent_FreeExpertPickQuery } from '@lib/graphql-types';
import { getGameAbbr } from '@utils/gameUtils';
import { observeVisibilityAndTrigger } from '@utils/html';
import { generateRandomAlphaNumericId, getValueOrDefault } from '@utils/commonUtils';
import { fromFreeExpertPicksToSidelinesData, isFreeExpertPickBettable } from '@utils/sidelineUtils';
import { eventsName, PickSnippetEventViewed, trackEvent } from '@utils/trackingUtils';

@Component({
  tag: 'sportsline-free-expert-pick',
  styleUrl: 'sportsline-free-expert-pick.css',
  assetsDirs: ['assets'],
  shadow: true,
})

/**
 * SportslineFreeExpertPick is a web component that displays a free expert pick card.
 * It fetches data from a GraphQL API and renders the pick details, including the expert's information,
 * game details, and upsell options for non-subscribers.
 *
 * @component
 * @prop {string} expertPickId - The unique identifier of the expert pick to be retrieved from the API. This is required.
 * @prop {string} [subscribeLink] - The URL to the subscription page. If not provided, a default link will be used.
 * @prop {ClientName} [clientName] - The name of the client, used to customize links and text based on the client.
 * @prop {string} [cardTitle] - Custom title for the card. If not provided, a default title will be displayed.
 * @prop {string} [upsellText] - Custom text to be displayed in the upsell section for non-subscribers. If not provided, a default text will be used.
 *
 * @state {object} expertPick - The expert pick data retrieved from the API, including game and expert details.
 * @state {boolean} loading - Indicates whether the component is currently loading data.
 * @state {string | null} error - Stores any error message encountered during data fetching.
 *
 * @method fetch - Fetches the expert pick data from the API using the provided `expertPickId`.
 * @method getSubscriberLink - Computes the subscription link based on the `subscribeLink` and `clientName` props.
 * @method getGameHref - Generates the game link based on the expert pick's game information and client name.
 * @method getCardTitle - Returns the card title, using the custom title if provided, or a default title otherwise.
 * @method getNeutralGameAbbr - Computes a neutral abbreviation for the game, ensuring "vs." is used for the button.
 * @method getUpsellText - Retrieves the upsell text based on the `clientName` and `upsellText` props.
 *
 * @lifecycle componentWillLoad - Invoked before the component is loaded. It validates the `expertPickId` and triggers data fetching.
 *
 * @render Renders the expert pick card with the following sections:
 * - Header: Displays the expert's information, card title, and a button for subscribers to view all picks.
 * - Selection: Displays the expert's pick details.
 * - Footer: Displays an upsell section for non-subscribers, including a subscription link and upsell text.
 *
 * @example
 * <sportsline-free-expert-pick
 *   expertPickId="12345"
 *   subscribeLink="https://example.com/subscribe"
 *   clientName="Sportsline"
 *   cardTitle="Custom Card Title"
 *   upsellText="Subscribe now to see all picks!"
 * ></sportsline-free-expert-pick>
 */
export class SportslineFreeExpertPick {
  /**
   * The ID of the pick that will be retrieved from helios.
   */
  @Prop() expertPickId: string;

  /**
   * The ID of the pick that will be retrieved from helios.
   */
  @Prop() gameUrl?: string;

  /**
   * The URL to the subscription page.
   */
  @Prop() subscribeLink?: string;

  /**
   * The name of the client, used to customize links and text based on the client.
   */
  @Prop() clientName?: ClientName;

  /**
   * Title for the card to replace the default.
   */
  @Prop() cardTitle?: string;

  /**
   * The text to be displayed in the upsell section to replace the default based on the clientName.
   */
  @Prop() upsellText?: string;

  /**
   * Is Bettable
   */
  @Prop() isBettable?: boolean = false;

  @State() expertPick: SportslinePickProps & {
    isSubscriber?: SportslineWebComponent_FreeExpertPickQuery['freeExpertPick']['isSubscriber'];
  };

  @State() data: SportslineWebComponent_FreeExpertPickQuery['freeExpertPick'] | null = null;
  @State() isPickBettable: boolean = false;

  @State() loading: boolean = true;
  @State() error: string | null = null;

  @Element() el: HTMLSportslineFreeExpertPickElement;

  private instanceId: string = generateRandomAlphaNumericId();
  private version: string = '1.0.0';
  private name: string = 'pick-of-the-day';
  private client = sportslineGraphQLClient();

  private get getSubscriberLink() {
    return getPropertySubscribeLink({
      subscribeLink: this.subscribeLink,
      clientName: this.clientName,
    });
  }

  private get getWidgetDataInfo() {
    return {
      name: this.name,
      instanceId: this.instanceId,
      version: this.version,
      ref: this.el,
    };
  }

  private trackError(error: Error) {
    trackEvent({
      action: 'error',
      eventName: eventsName.martech,
      payload: {
        error: error.message,
      },
      widgetData: this.getWidgetDataInfo,
    });
  }

  private get getGameHref() {
    return getValueOrDefault(
      this.gameUrl,
      getPropertyGameHref({
        clientName: this.clientName,
        leagueAbbr: this.expertPick.gameInfo?.league.abbrev,
        gameAbbr: this.expertPick.gameInfo?.abbrev,
      }),
    );
  }

  private get getGameUrlTarget() {
    return getPropertyGameUrlTarget(this.clientName);
  }

  private get getCardTitle() {
    const leagueAbbr = this.expertPick.gameInfo?.league?.abbrev?.toUpperCase();
    return getCardTitle({
      title: this.cardTitle,
      leagueAbbr,
    });
  }

  private get getNeutralGameAbbr() {
    // force to use vs. for the button
    return getGameAbbr({ ...this.expertPick.gameInfo, isNeutral: true });
  }

  private get getUpsellText() {
    return getPropertyUpsellText({
      clientName: this.clientName,
      upsellText: this.upsellText,
    });
  }

  private async fetch() {
    this.loading = true;
    this.error = null;

    await this.client.dataFetcher
      .SportslineWebComponent_FreeExpertPick({
        expertPickId: this.expertPickId,
      })
      .then(response => {
        const data = response.data;
        // If there is any error in the response, throw an error
        if (response.errors?.length > 0) {
          console.warn('Error fetching data:', response.errors);
          throw new Error(`GraphQL errors: ${response.errors.map(error => error.message).join(', ')}`);
        }
        if (data && Boolean(data.freeExpertPick)) {
          // check if has valid data for vendor: fromFreeExpertPicksToSidelinesData(this.data)
          const isBettable = this.isBettable;
          const isFreeExpertPickBettableResponse = isFreeExpertPickBettable(data.freeExpertPick);
          this.isPickBettable = Boolean(isBettable && isFreeExpertPickBettableResponse);
          this.data = data.freeExpertPick;
          this.expertPick = {
            ...fromSportslineWebComponent_FreeExpertPickQueryToSportslinePickProps(data),
            isSubscriber: data.freeExpertPick.isSubscriber,
            isBettable: this.isPickBettable,
          };
          const bet = fromFreeExpertPickToTrackingViewedWidgetDataBet(data.freeExpertPick);
          const event: PickSnippetEventViewed = {
            action: 'viewed',
            eventName: eventsName.martech,
            widgetData: {
              ...this.getWidgetDataInfo,
              bets: [bet],
            },
          };
          trackEvent(event);

          return;
        }
        // If the data is null or undefined, set error state
        this.error = null;
        console.warn(`No data found for the provided expert pick ID ${this.expertPickId}`);
      });
  }
  private get renderVendor() {
    if (this.el.getElementsByClassName('cbs-widget').length > 0) {
      return;
    }
    // there is a check happening on fetch that wont allow this to run if the data is not valid
    const sidelinesData = fromFreeExpertPicksToSidelinesData(this.data);
    const sidelines = document.createElement('div');
    sidelines.classList.add('cbs-widget');
    sidelines.setAttribute(
      'data-config',
      JSON.stringify({
        ...sidelinesData,
        instanceId: this.instanceId,
      }),
    );
    this.el.appendChild(sidelines);
  }

  async componentWillLoad() {
    if (!Boolean(this.expertPickId)) {
      this.error = 'No expert pick ID provided';
      this.loading = false;
      this.trackError(new Error('No expert pick ID provided'));
      return;
    }
    trackEvent({
      action: 'impression',
      eventName: eventsName.martech,
      widgetData: {
        ...this.getWidgetDataInfo,
        bets: [],
      },
    });

    observeVisibilityAndTrigger({
      element: this.el,
      callback: async () => {
        await this.fetch()
          .catch((error: Error) => {
            // Track error if fetching fails
            console.warn('Error fetching data:', error);
            this.error = 'An error occurred while fetching data. Please try again later.';
            this.trackError(error);
          })
          .finally(() => {
            this.loading = false;
          });
      },
    });
  }

  render() {
    if (!Boolean(this.expertPick) && !this.loading) {
      if (Boolean(this.error)) {
        return (
          <Host>
            <div>{this.error}</div>
            <slot></slot>
          </Host>
        );
      }
      return null;
    }

    if (this.loading) {
      return (
        <Host>
          <div
            class={{
              'sportsline-free-expert-pick--loading': true,
            }}
          >
            <sportsline-circular-progress size="sm" />
          </div>
        </Host>
      );
    }

    if (this.isPickBettable) {
      return (
        <Host>
          <sportsline-free-expert-pick-card pick={this.expertPick} neutralGameAbbr={this.getNeutralGameAbbr} cardTitle={this.getCardTitle} gameUrlTarget={this.getGameUrlTarget}>
            <slot></slot>
          </sportsline-free-expert-pick-card>
          {this.renderVendor}
        </Host>
      );
    }

    return (
      <Host>
        <sportsline-free-expert-pick-card
          pick={this.expertPick}
          gameUrl={this.getGameHref}
          neutralGameAbbr={this.getNeutralGameAbbr}
          cardTitle={this.getCardTitle}
          subscriberLink={this.getSubscriberLink}
          upsellText={this.getUpsellText}
          isSubscriber={this.expertPick.isSubscriber}
          gameUrlTarget={this.getGameUrlTarget}
        />
      </Host>
    );
  }
}
